<script lang="ts">
import { defineComponent, PropType } from 'vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import ButtonPrev from '@/components/button/ButtonPrev.vue'
import InputCompanyName from '@/components/lead/InputCompanyName.vue'
import InputFormDepartmentAndPosition from '@/components/lead/InputFormDepartmentAndPosition.vue'
import SelectorIndustry from '@/components/lead/SelectorIndustry.vue'
import SelectorPositionLevel from '@/components/lead/SelectorPositionLevel.vue'
import SelectorProfession from '@/components/lead/SelectorProfession.vue'
import SelectorProfessionWithDomain from '@/components/lead/SelectorProfessionWithDomain.vue'
import PaginationRegistrationStep from '@/components/user/registration/PaginationRegistrationStep.vue'
import ValidationObserver from '@/components/validation/ValidationObserver.vue'
import validationOperator from '@/models/validationOperator'

export default defineComponent({
  name: 'InputStep2UserRegistrationForShowContents',

  components: {
    ButtonSubmit,
    ButtonPrev,
    InputCompanyName,
    InputFormDepartmentAndPosition,
    SelectorIndustry,
    SelectorPositionLevel,
    SelectorProfession,
    SelectorProfessionWithDomain,
    PaginationRegistrationStep,
    ValidationObserver,
  },

  props: {
    namespace: {
      type: String,
      default: '',
    },

    companyName: {
      type: Object as PropType<InputText>,
      required: true,
    },

    industry: {
      type: Object as PropType<InputText>,
      required: true,
    },

    profession: {
      type: Object as PropType<InputText>,
      required: true,
    },

    professionDomain: {
      type: Object as PropType<InputMultiSelect>,
      required: true,
    },

    positionLevel: {
      type: Object as PropType<InputText>,
      required: true,
    },

    departmentAndPosition: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateZipcode: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateAddress1: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateAddress2: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateNumber: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateEmployeeNum: {
      type: Object as PropType<InputNumber>,
      required: true,
    },
  },

  emits: [
    'update:companyName',
    'update:industry',
    'update:profession',
    'update:professionDomain',
    'open:professionDomain',
    'close:professionDomain',
    'update:positionLevel',
    'update:departmentAndPosition',
    'select:companyLeadInfo',
    'click:next',
    'click:prev',
  ],

  setup(props, { emit }) {
    const { validationObserver, validate } = validationOperator()
    const next = () => {
      emit('click:next')
    }

    return {
      // data
      validationObserver,

      // computed

      // methods
      validate,
      next,
    }
  }
})
</script>

<template>
  <ValidationObserver
    ref="validationObserver"
    :namespace="namespace"
    @valid="next">
    <div class="o-heading__section">
      <h2 class="o-heading__section--item">
        <slot name="heading">
          資料を読むには次の項目をご入力ください
        </slot>
      </h2>
    <!-- /.o-heading__section --></div>

    <PaginationRegistrationStep :current-step="2" />

    <div class="l-rows">
      <!-- 勤務先名 -->
      <div
        v-if="companyName"
        class="l-rows--row">
        <InputCompanyName
          :model-value="companyName.value"
          :corporate-zipcode="corporateZipcode!"
          :corporate-address1="corporateAddress1!"
          :corporate-address2="corporateAddress2!"
          :corporate-number="corporateNumber!"
          :corporate-employee-num="corporateEmployeeNum!"
          :namespace="namespace"
          :name="companyName.name"
          :validation-scheme="companyName.validationScheme"
          @update:model-value="$emit('update:companyName', $event)"
          @select:company-lead-info="$emit('select:companyLeadInfo', $event)" />
      <!-- /.l-rows row --></div>

      <!-- 業種 -->
      <div
        v-if="industry"
        class="l-rows--row">
        <SelectorIndustry
          :industry="industry"
          :namespace="namespace"
          @update:industry="$emit('update:industry', $event)" />
      <!-- /.l-rows row --></div>

      <!-- 職種 -->
      <div
        v-if="profession"
        class="l-rows--row">
        <SelectorProfessionWithDomain
          v-if="professionDomain"
          :profession="profession"
          :profession-domain="professionDomain"
          :namespace="namespace"
          @update:profession="$emit('update:profession', $event)"
          @update:profession-domain="$emit('update:professionDomain', $event)"
          @open:profession-domain="$emit('open:professionDomain')"
          @close:profession-domain="$emit('close:professionDomain')" />

        <SelectorProfession
          v-else
          :profession="profession"
          :namespace="namespace"
          @update:profession="$emit('update:profession', $event)" />
      <!-- /.l-rows row --></div>

      <!-- 役職 -->
      <div
        v-if="positionLevel"
        class="l-rows--row">
        <SelectorPositionLevel
          :position-level="positionLevel"
          :namespace="namespace"
          @update:position-level="$emit('update:positionLevel', $event)" />
      <!-- /.l-rows row --></div>

      <!-- 部署・役職正式名称 -->
      <div
        v-if="departmentAndPosition"
        class="l-rows--row">
        <InputFormDepartmentAndPosition
          :department-and-position="departmentAndPosition"
          :namespace="namespace"
          @update:model-value="$emit('update:departmentAndPosition', $event)" />
      <!-- /.l-rows row --></div>
    <!-- /.l-rows --></div>

    <ButtonSubmit
      :is-link="false"
      :has-shadow="false"
      prevent
      stop
      @click:submit="validate">
      次へ
    </ButtonSubmit>

    <ButtonPrev
      :is-link="false"
      @click:prev="$emit('click:prev')" />
  </ValidationObserver>
</template>

<style lang="sass" scoped>
// object
.o-heading__section
  & &--item
    font:
      size: $font-normal
      weight: bold
    text-align: center

  + .p
    margin-top: 0.5em
</style>
